import React, { useEffect, useState } from 'react';
import { Link } from 'gatsby';
import { useLocation } from '@reach/router';
import classNames from 'classnames';
import globalStrings from '../../config/index';
import EnsembleLogo from './EnsembleLogo';
import MobileMenu from './MobileMenu';
import { useRegion } from '../../region/RegionProvider';
import GeolocationDropdown from '../geolocation/GeolocationDropdown';

const NavBar: React.FC = () => {
  const { region } = useRegion();
  const prefix = region === 'global' ? '' : `/emea`;

  const { logoAlt, services, products, portfolio, about, careers, contact } =
    globalStrings.navigation;

  const location = useLocation();

  const rawPath = location?.pathname || '/';
  const normalized = rawPath.replace(/\/+$/, '') || '/';

  const isHome = normalized === '/' || normalized === '/emea';
  const isCareers = normalized === '/careers' || normalized === '/emea/careers';

  const shouldOverlayHero = isHome || isCareers;

  const [isOpen, setIsOpen] = useState(false);
  const [isTransparent, setIsTransparent] = useState(shouldOverlayHero);

  const toggleMenu = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    document.body.style.overflowY = newIsOpen ? 'hidden' : 'auto';
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!shouldOverlayHero) {
      setIsTransparent(false);
      return;
    }

    setIsTransparent(true);

    const heroSelector = isCareers
      ? '[data-testid="heroc-container"]'
      : '[data-testid="hero-container"]';

    const heroEl = document.querySelector(heroSelector);

    if (!heroEl) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsTransparent(entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0,
        rootMargin: '-100px 0px 0px 0px',
      }
    );

    observer.observe(heroEl);

    return () => {
      observer.disconnect();
    };
  }, [shouldOverlayHero, isCareers, location.pathname]);

  const menuItemBase =
    'text-sm border-transparent border-b uppercase font-normal leading-[19px] hover:border-ensemble-orange transition-colors duration-300';

  const menuItemColor = isTransparent ? 'text-white' : 'text-[#333333]';
  const menuItemClassCss = classNames(menuItemBase, menuItemColor);

  const activeMenuItemClassCss = isTransparent
    ? classNames('font-semibold !border-white !text-white')
    : classNames('font-semibold !border-ensemble-orange');

  const containerMenuItemClassCss = classNames(
    'min-w-[70px] flex justify-center'
  );

  return (
    <div
      data-testid="navbar"
      className={classNames(
        'md:px-20 sm:px-8 px-4 top-0 left-0 right-0 z-[100] pt-10 transition-none',
        shouldOverlayHero ? 'fixed' : 'sticky',
        {
          'bg-transparent': isTransparent,
          'bg-white': !isTransparent,
        }
      )}
    >
      <div className="container mx-auto py-5 flex items-center justify-between lg:py-7">
        <Link className="flex items-center flex-shrink-0" to="/">
          <EnsembleLogo isWhite={isTransparent} alt={logoAlt} />
        </Link>

        <MobileMenu onClick={toggleMenu} isTransparent={isTransparent} />

        <div className="flex items-center space-x-[33px] hidden lg:flex">
          <div className={containerMenuItemClassCss}>
            <Link
              className={menuItemClassCss}
              to={`${prefix}/services`}
              activeClassName={activeMenuItemClassCss}
              partiallyActive
            >
              {services}
            </Link>
          </div>

          <div className={containerMenuItemClassCss}>
            <Link
              className={menuItemClassCss}
              to={`${prefix}/portfolio`}
              activeClassName={activeMenuItemClassCss}
              partiallyActive={false}
            >
              {portfolio}
            </Link>
          </div>

          <div className={containerMenuItemClassCss}>
            <Link
              className={menuItemClassCss}
              to={`${prefix}/products`}
              activeClassName={activeMenuItemClassCss}
              partiallyActive={false}
            >
              {products}
            </Link>
          </div>

          <div className={containerMenuItemClassCss}>
            <Link
              className={menuItemClassCss}
              to={`${prefix}/about`}
              activeClassName={activeMenuItemClassCss}
              partiallyActive
            >
              {about}
            </Link>
          </div>

          <div className={containerMenuItemClassCss}>
            <Link
              className={menuItemClassCss}
              to={`${prefix}/careers`}
              activeClassName={activeMenuItemClassCss}
              partiallyActive
            >
              {careers}
            </Link>
          </div>

          <div className={containerMenuItemClassCss}>
            <Link
              className={menuItemClassCss}
              to={`${prefix}/contact`}
              activeClassName={activeMenuItemClassCss}
              partiallyActive
            >
              {contact}
            </Link>
          </div>

          <GeolocationDropdown />
        </div>
      </div>
    </div>
  );
};

export default NavBar;
